import React, { useEffect, useRef, useState } from 'react';
import {
  motion,
  useInView,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  useReducedMotion,
} from 'framer-motion';

/**
 * ImpactNumbers — four honest, defensible industry stats.
 *
 * Restrained editorial cards (NOT a SaaS box grid). Asymmetric 2×2 layout
 * with vertical offset between the two columns so the rhythm reads as
 * hand-set. Each card: huge Fraunces serif value, thin sage hairline,
 * quiet muted supporting line. Cursor-tilt on desktop, static on mobile.
 */

// ---------- Honest, defensible stats (unchanged from the pre-2/7 version) ----------
const STATS = [
  {
    value: '8–14',
    suffix: '%',
    label: 'Typical EBITDA range for well-run Indian restaurants — every percentage point of leakage matters.',
  },
  {
    value: '25–45',
    suffix: '%',
    label: 'Share of restaurant revenue now flowing through aggregators and digital payments — where settlement variances commonly arise.',
  },
  {
    value: 'Auto',
    suffix: '',
    label: 'POS data ingestion. We pull and process reports automatically — no daily exports asked of your operator.',
  },
  {
    value: 'Mon · 7th',
    suffix: '',
    label: 'Weekly Brief lands every Monday. Monthly Detailed Report on the seventh. A cadence you can plan around.',
  },
];

// Intentional irregular base delays (not 0, 0.1, 0.2, 0.3)
const CARD_DELAY = [0.00, 0.17, 0.34, 0.48];

// ---------- Palette ----------
const BG    = '#0F0B0A';
const BONE  = '#F2E8D2';
const MUTED = 'rgba(242, 232, 210, 0.58)';
const SAGE  = '#9CC6AE';

// ---------- useCanHover (pointer-fine detection) ----------
const useCanHover = () => {
  const [canHover, setCanHover] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const onChange = () => setCanHover(mq.matches);
    onChange();
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return canHover;
};

// ============================================================
// StatCard — editorial card with heavy/laggy cursor tilt
// ============================================================
const StatCard = ({ stat, index, reduced, canHover }) => {
  const cardRef = useRef(null);
  const tiltRef = useRef(null);
  const inView = useInView(cardRef, { once: true, amount: 0.35, margin: '0px 0px -8% 0px' });

  // Heavy / laggy tilt (low stiffness, high mass)
  const rxMV = useMotionValue(0);
  const ryMV = useMotionValue(0);
  const rx = useSpring(rxMV, { stiffness: 55, damping: 18, mass: 1.2 });
  const ry = useSpring(ryMV, { stiffness: 55, damping: 18, mass: 1.2 });

  const shadowX     = useTransform(ry, [-6, 6], [-10, 10]);
  const shadowY     = useTransform(rx, [-4, 4], [14, -2]);
  const shadowBlur  = useTransform([rx, ry], ([a, b]) => 28 + Math.abs(a) * 1.4 + Math.abs(b) * 1.4);
  const shadowAlpha = useTransform([rx, ry], ([a, b]) => 0.18 + (Math.abs(a) + Math.abs(b)) * 0.018);
  const boxShadow = useMotionTemplate`${shadowX}px ${shadowY}px ${shadowBlur}px -10px rgba(0, 0, 0, ${shadowAlpha})`;

  const tiltActive = canHover && !reduced;

  const handleMove = (e) => {
    if (!tiltActive || !tiltRef.current) return;
    const r = tiltRef.current.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width  - 0.5;
    const ny = (e.clientY - r.top)  / r.height - 0.5;
    ryMV.set(nx * 12);   // ±6°
    rxMV.set(-ny * 8);   // ±4°
  };
  const handleLeave = () => { rxMV.set(0); ryMV.set(0); };

  return (
    <div ref={cardRef} data-testid={`impact-stat-${index}`}>
      <div style={{ perspective: tiltActive ? 1200 : 'none' }}>
        <motion.div
          ref={tiltRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          initial={reduced ? false : { opacity: 0, y: 26 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 26 }}
          // ζ ≈ 0.97 — calm with a hair of overshoot ("real weight")
          transition={
            reduced
              ? { duration: 0.5 }
              : { type: 'spring', stiffness: 85, damping: 18, mass: 0.8, delay: CARD_DELAY[index] }
          }
          style={
            tiltActive
              ? { rotateX: rx, rotateY: ry, transformStyle: 'preserve-3d', boxShadow, borderRadius: 14 }
              : undefined
          }
          className="relative pt-8 md:pt-10 pb-2"
        >
          {/* Hair line at top of card — sets the "ledger entry" tone */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 h-px"
            style={{ width: '88px', background: SAGE, opacity: 0.55 }}
          />

          {/* Big Fraunces value */}
          <div
            className="leading-[0.95] select-none mb-7 md:mb-9"
            style={{
              fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
              fontWeight: 500,
              fontVariantNumeric: 'oldstyle-nums',
              fontSize: 'clamp(40px, 4.6vw, 72px)',
              color: BONE,
              letterSpacing: '-0.022em',
            }}
          >
            {stat.value}
            {stat.suffix && (
              <span style={{ fontSize: '0.5em', verticalAlign: '0.55em', marginLeft: '0.04em', opacity: 0.62 }}>
                {stat.suffix}
              </span>
            )}
          </div>

          {/* Supporting line — quiet, restrained */}
          <p
            className="text-[14.5px] md:text-[15.5px] leading-relaxed max-w-sm"
            style={{
              color: MUTED,
              fontFamily: "'Manrope', system-ui, sans-serif",
              fontWeight: 400,
            }}
          >
            {stat.label}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================================
// Section
// ============================================================
export const ImpactNumbers = () => {
  const reduced = useReducedMotion();
  const canHover = useCanHover();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section
      ref={ref}
      id="impact"
      data-testid="landing-impact"
      className="relative py-32 md:py-44 overflow-hidden"
      style={{ backgroundColor: BG }}
    >
      {/* Subtle film grain — keeps the dark from going flat */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-screen"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='220' height='220'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.5' numOctaves='2' seed='9' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Asymmetric warm wash from upper-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 80% 18%, rgba(207, 185, 135, 0.06) 0%, rgba(207, 185, 135, 0.02) 35%, rgba(15, 11, 10, 0) 70%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-6 md:px-10">
        {/* Editorial header */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
          transition={
            reduced
              ? { duration: 0.5 }
              : { type: 'spring', stiffness: 78, damping: 19, mass: 0.9 }
          }
          className="max-w-3xl mb-24 md:mb-32"
        >
          <div className="text-[10px] md:text-xs uppercase tracking-[0.34em] font-medium mb-6 flex items-center gap-3" style={{ color: 'rgba(156, 198, 174, 0.72)' }}>
            <span className="block w-10 h-px" style={{ background: SAGE, opacity: 0.5 }} />
            Why It Matters
          </div>
          <h2
            className="tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-[60px]"
            style={{ color: BONE, fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif", fontWeight: 500 }}
          >
            What the numbers say.
          </h2>
          <p
            className="text-base md:text-lg mt-7 leading-relaxed max-w-2xl"
            style={{ color: MUTED, fontFamily: "'Manrope', system-ui, sans-serif" }}
          >
            We don&apos;t make recovery promises — every engagement is different. What we do is take publicly-known industry realities and apply a financial process to each client&apos;s specific books.
          </p>
        </motion.div>

        {/* Asymmetric 2×2 grid — right column is offset downward so the
            rhythm reads as hand-set, not a flat SaaS row. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 lg:gap-x-20 gap-y-20 md:gap-y-24">
          {/* Left column */}
          <div className="md:pt-0">
            <StatCard stat={STATS[0]} index={0} reduced={!!reduced} canHover={canHover} />
            <div className="h-20 md:h-28" />
            <StatCard stat={STATS[2]} index={2} reduced={!!reduced} canHover={canHover} />
          </div>
          {/* Right column — visually anchored lower for intentional asymmetry */}
          <div className="md:pt-24 lg:pt-32">
            <StatCard stat={STATS[1]} index={1} reduced={!!reduced} canHover={canHover} />
            <div className="h-20 md:h-28" />
            <StatCard stat={STATS[3]} index={3} reduced={!!reduced} canHover={canHover} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactNumbers;
